export type SoundName =
  | 'roll'
  | 'move'
  | 'settle'
  | 'capture'
  | 'effect'
  | 'portal'
  | 'crack'
  | 'shield'
  | 'wait'
  | 'extra'
  | 'goal'
  | 'win'
  | 'join'
  | 'click'
  | 'error';

interface AudioEngine {
  context: AudioContext;
  sfx: GainNode;
  music: GainNode;
  master: DynamicsCompressorNode;
  noise: AudioBuffer;
}

let engine: AudioEngine | null = null;
let musicTimer: number | null = null;
let musicChord = 0;
let musicRequested = false;
let musicLevel = 0.55;

const CHORDS: number[][] = [
  [73.42, 110, 146.83, 185],
  [61.74, 92.5, 123.47, 146.83],
  [98, 146.83, 196, 246.94],
  [110, 164.81, 220, 277.18],
];

const ARPEGGIO = [220, 261.63, 329.63, 392, 523.25, 392, 329.63, 261.63];

function createNoise(context: AudioContext): AudioBuffer {
  const buffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let index = 0; index < channel.length; index += 1) {
    channel[index] = Math.random() * 2 - 1;
  }
  return buffer;
}

function getEngine(): AudioEngine | null {
  if (typeof window === 'undefined') return null;
  const AudioContextClass =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!engine) {
    const context = new AudioContextClass();
    const master = context.createDynamicsCompressor();
    master.threshold.value = -16;
    master.knee.value = 18;
    master.ratio.value = 4;
    master.attack.value = 0.004;
    master.release.value = 0.24;
    master.connect(context.destination);

    const sfx = context.createGain();
    const music = context.createGain();
    const musicFilter = context.createBiquadFilter();
    const delay = context.createDelay(1);
    const feedback = context.createGain();
    const wet = context.createGain();
    sfx.gain.value = 0.75;
    music.gain.value = musicLevel;
    musicFilter.type = 'lowpass';
    musicFilter.frequency.value = 8200;
    musicFilter.Q.value = 0.5;
    delay.delayTime.value = 0.37;
    feedback.gain.value = 0.23;
    wet.gain.value = 0.18;
    sfx.connect(master);
    music.connect(musicFilter);
    musicFilter.connect(master);
    music.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(wet);
    wet.connect(master);
    engine = { context, sfx, music, master, noise: createNoise(context) };
  }
  if (engine.context.state === 'suspended') void engine.context.resume();
  return engine;
}

export function unlockAudio(): void {
  const audio = getEngine();
  if (!audio) return;
  void audio.context.resume();
  if (musicRequested) startMusic(true, musicLevel);
}

export function setSfxVolume(volume: number): void {
  const audio = getEngine();
  if (!audio) return;
  audio.sfx.gain.setTargetAtTime(Math.max(0, Math.min(1, volume)), audio.context.currentTime, 0.03);
}

export function setMusicVolume(volume: number): void {
  musicLevel = Math.max(0, Math.min(1, volume));
  const audio = getEngine();
  if (!audio) return;
  audio.music.gain.setTargetAtTime(musicRequested ? musicLevel : 0, audio.context.currentTime, 0.12);
}

function tone(
  audio: AudioEngine,
  frequency: number,
  startOffset: number,
  duration: number,
  type: OscillatorType,
  volume: number,
  destination: GainNode,
  endFrequency?: number,
): void {
  const start = audio.context.currentTime + startOffset;
  const oscillator = audio.context.createOscillator();
  const gain = audio.context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  if (endFrequency !== undefined) {
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), start + duration);
  }
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.03);
}

function noiseBurst(
  audio: AudioEngine,
  startOffset: number,
  duration: number,
  volume: number,
  frequency = 900,
  type: BiquadFilterType = 'bandpass',
  destination: GainNode = audio.sfx,
): void {
  const start = audio.context.currentTime + startOffset;
  const source = audio.context.createBufferSource();
  const filter = audio.context.createBiquadFilter();
  const gain = audio.context.createGain();
  source.buffer = audio.noise;
  filter.type = type;
  filter.frequency.value = frequency;
  filter.Q.value = 0.8;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, volume), start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  source.start(start);
  source.stop(start + duration + 0.02);
}

export function playSound(name: SoundName, enabled: boolean): void {
  if (!enabled) return;
  try {
    const audio = getEngine();
    if (!audio) return;
    const sfx = audio.sfx;

    switch (name) {
      case 'roll':
        // Madera: golpes secos de caja, rebotes y un cuerpo grave de mesa.
        [0, 0.07, 0.15, 0.24, 0.35].forEach((offset, index) => {
          noiseBurst(audio, offset, 0.045, 0.1 - index * 0.012, 900 + index * 220, 'bandpass');
          tone(audio, 240 + index * 46, offset, 0.075, 'triangle', 0.055 - index * 0.006, sfx, 160 + index * 26);
        });
        tone(audio, 104, 0.02, 0.24, 'sine', 0.055, sfx, 58);
        tone(audio, 196, 0.22, 0.18, 'sine', 0.035, sfx, 122);
        noiseBurst(audio, 0.42, 0.22, 0.035, 620, 'lowpass');
        break;
      case 'move':
        tone(audio, 340, 0, 0.2, 'triangle', 0.03, sfx, 720);
        noiseBurst(audio, 0, 0.18, 0.028, 1800, 'bandpass');
        tone(audio, 1046.5, 0.12, 0.12, 'sine', 0.018, sfx, 1568);
        break;
      case 'settle':
        noiseBurst(audio, 0, 0.05, 0.055, 5200, 'highpass');
        tone(audio, 1174.66, 0, 0.13, 'sine', 0.035, sfx);
        tone(audio, 1760, 0.035, 0.11, 'sine', 0.022, sfx);
        tone(audio, 587.33, 0.01, 0.18, 'triangle', 0.024, sfx, 440);
        tone(audio, 293.66, 0, 0.2, 'sine', 0.025, sfx, 220);
        break;
      case 'capture':
        // Breve campo de batalla: choques metálicos, proyectiles y golpe final.
        noiseBurst(audio, 0, 1.9, 0.035, 320, 'lowpass');
        [0.02, 0.28, 0.55, 0.82, 1.15].forEach((offset, index) => {
          noiseBurst(audio, offset, 0.08, 0.075 - index * 0.009, 1800 + index * 320, 'bandpass');
          tone(audio, 1560 + index * 190, offset, 0.11, 'triangle', 0.03, sfx, 920 + index * 120);
          tone(audio, 248 + index * 18, offset, 0.08, 'square', 0.022, sfx, 180);
        });
        [0.12, 0.44, 0.76, 1.02].forEach((offset, index) => {
          tone(audio, 980 + index * 90, offset, 0.16, 'sine', 0.018, sfx, 240 + index * 30);
          noiseBurst(audio, offset, 0.12, 0.025, 2400, 'highpass');
        });
        tone(audio, 196, 1.25, 0.7, 'sawtooth', 0.07, sfx, 42);
        noiseBurst(audio, 1.25, 0.42, 0.15, 380, 'lowpass');
        [1760, 2093, 2637, 3136].forEach((frequency, index) => {
          tone(audio, frequency, 1.28 + index * 0.055, 0.5, 'sine', 0.025, sfx, frequency * 0.72);
        });
        tone(audio, 523.25, 1.3, 0.75, 'triangle', 0.03, sfx, 261.63);
        break;
      case 'portal':
        tone(audio, 146.83, 0, 1.1, 'sawtooth', 0.02, sfx, 293.66);
        [293.66, 440, 587.33, 880, 1174.66].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.13, 0.7, 'triangle', 0.028, sfx, frequency * 1.5);
        });
        noiseBurst(audio, 0.02, 0.9, 0.04, 2600, 'highpass');
        [2093, 2637, 3136].forEach((frequency, index) => {
          tone(audio, frequency, 0.75 + index * 0.06, 0.18, 'sine', 0.012, sfx);
        });
        break;
      case 'crack':
        tone(audio, 82, 0, 0.9, 'sawtooth', 0.07, sfx, 28);
        tone(audio, 164, 0.03, 0.5, 'square', 0.03, sfx, 55);
        noiseBurst(audio, 0, 0.55, 0.13, 240, 'lowpass');
        noiseBurst(audio, 0.08, 0.2, 0.07, 880, 'bandpass');
        [1760, 2349, 3136].forEach((frequency, index) => {
          tone(audio, frequency, 0.35 + index * 0.08, 0.25, 'triangle', 0.018, sfx, frequency * 0.65);
        });
        break;
      case 'shield':
        [523.25, 783.99, 1046.5, 1567.98].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.065, 0.6, 'sine', 0.035 - index * 0.004, sfx);
        });
        tone(audio, 261.63, 0, 0.45, 'triangle', 0.03, sfx, 196);
        noiseBurst(audio, 0, 0.22, 0.025, 3600);
        break;
      case 'wait':
        [659.25, 523.25, 392, 261.63].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.14, 0.4, 'sine', 0.035, sfx, frequency * 0.82);
        });
        tone(audio, 130.81, 0.4, 0.5, 'triangle', 0.035, sfx, 98);
        break;
      case 'extra':
        [392, 523.25, 659.25, 880, 1174.66].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.07, 0.28, 'triangle', 0.03, sfx, frequency * 1.02);
        });
        noiseBurst(audio, 0.2, 0.3, 0.02, 5200, 'highpass');
        break;
      case 'effect':
        [440, 554.37, 659.25, 880].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.06, 0.25, 'sine', 0.028, sfx);
        });
        break;
      case 'goal':
      case 'win':
        [261.63, 392, 523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
          tone(audio, frequency, index * 0.1, 0.75, 'triangle', 0.042, sfx, frequency * 1.01);
          tone(audio, frequency * 2, index * 0.1 + 0.02, 0.45, 'sine', 0.014, sfx);
        });
        tone(audio, 130.81, 0, 1.2, 'sawtooth', 0.025, sfx, 196);
        noiseBurst(audio, 0.25, 1.2, 0.035, 3200, 'highpass');
        break;
      case 'join':
        tone(audio, 392, 0, 0.12, 'sine', 0.03, sfx);
        tone(audio, 523.25, 0.1, 0.2, 'sine', 0.035, sfx);
        break;
      case 'click':
        tone(audio, 720, 0, 0.035, 'sine', 0.018, sfx);
        break;
      case 'error':
        tone(audio, 196, 0, 0.18, 'square', 0.03, sfx, 147);
        break;
    }
  } catch {
    // El audio es una mejora opcional; el juego nunca depende de él.
  }
}

function scheduleMusicBar(): void {
  const audio = getEngine();
  if (!audio || !musicRequested) return;
  const chord = CHORDS[musicChord % CHORDS.length] ?? CHORDS[0]!;
  musicChord += 1;
  chord.forEach((frequency, index) => {
    tone(audio, frequency, index * 0.09, 6.8, index < 2 ? 'triangle' : 'sine', 0.011, audio.music);
    tone(audio, frequency * 2, 0.18 + index * 0.06, 5.2, 'sine', 0.005, audio.music);
  });
  // Pulso cálido, menos percusivo que antes.
  [0, 1.7, 3.4, 5.1].forEach((offset) => {
    tone(audio, 52, offset, 0.28, 'sine', 0.045, audio.music, 36);
    noiseBurst(audio, offset + 0.02, 0.075, 0.005, 560, 'lowpass', audio.music);
  });
  for (let index = 0; index < 6; index += 1) {
    noiseBurst(audio, 0.5 + index * 1.05, 0.09, 0.0035, 4200 + (index % 2) * 800, 'highpass', audio.music);
  }
  const melody = [587.33, 739.99, 880, 739.99, 659.25, 880, 987.77, 739.99];
  for (let index = 0; index < 5; index += 1) {
    const note = melody[(musicChord + index) % melody.length] ?? 587.33;
    tone(audio, note, 0.65 + index * 1.18, 0.8, 'triangle', 0.009, audio.music);
  }
  tone(audio, 1174.66, 1.8, 0.9, 'sine', 0.004, audio.music, 1760);
  noiseBurst(audio, 0, 1.4, 0.003, 1000, 'lowpass', audio.music);
}

function beginMusicLoop(): void {
  if (musicTimer !== null || !musicRequested || !engine) return;
  scheduleMusicBar();
  musicTimer = window.setInterval(scheduleMusicBar, 6800);
}

export function startMusic(enabled: boolean, volume: number): void {
  musicLevel = Math.max(0, Math.min(1, volume));
  const audio = getEngine();
  if (!audio) return;
  if (!enabled) {
    stopMusic();
    return;
  }
  musicRequested = true;
  audio.music.gain.setTargetAtTime(musicLevel, audio.context.currentTime, 0.2);
  if (audio.context.state !== 'running') {
    void audio.context.resume().then(beginMusicLoop).catch(() => undefined);
    return;
  }
  beginMusicLoop();
}

export function stopMusic(): void {
  musicRequested = false;
  if (musicTimer !== null) {
    window.clearInterval(musicTimer);
    musicTimer = null;
  }
  if (!engine) return;
  engine.music.gain.setTargetAtTime(0, engine.context.currentTime, 0.25);
}
